import React, { ReactElement } from 'react';
import classNames from 'classnames';
import { omit } from 'lodash';

import useFallbackTranslation from '../../utils/hooks/useFallbackTranslation';

export type ButtonVariantType = 'primary' | 'secondary' | 'tertiary' | 'quaternary' | 'link';

interface DefaultProps {
  variant: ButtonVariantType;
  size: 'xs' | 's' | 'm' | 'l';
  iconPlacement?: 'right' | 'left';
  linkUnderline?: boolean;
  disabled: boolean;
  icon?: ReactElement;
  busy?: boolean;
  noKeyboard?: boolean;
  showSkeletonLoader?: boolean;
}

export interface BtnProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    Omit<DefaultProps, 'disabled'> {}

export interface LinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement>, DefaultProps {
  asLink?: boolean;
}

export interface DivProps extends React.HTMLAttributes<HTMLDivElement>, DefaultProps {
  asDiv?: boolean;
}

export type ButtonProps = BtnProps | LinkProps | DivProps;

const variantWithBorders: ButtonVariantType[] = ['primary', 'secondary'];

const sizeTextWithIconClassNames = {
  //The first key is the size of the button and the second is the direction of the icon
  xs: {
    right: 'mr-2 ml-4',
    left: 'mr-4 ml-2',
  },
  s: {
    right: 'mr-2 ml-4',
    left: 'mr-4 ml-2',
  },
  m: {
    right: 'mr-6 ml-10',
    left: 'mr-10 ml-6',
  },
  l: {
    right: 'mr-6 ml-10',
    left: 'mr-10 ml-6',
  },
};

const sizeTextClassNames = {
  xs: 'px-4',
  s: 'px-4',
  m: 'px-10',
  l: 'px-10',
};

const sizeButtonClassNames = {
  xs: 'h-6 min-w-6 text-[0.5rem]',
  s: 'h-8 min-w-8 text-[0.75rem]',
  m: 'h-12 min-w-12',
  l: 'h-14 min-h-14',
};

const sizeIconClassNames = {
  xs: 'h-full w-6',
  s: 'h-full w-8',
  m: 'h-full w-12',
  l: 'h-full w-14',
};

// The width of the icon should be smaller for buttons with border because content height is smaller
const sizeIconWithBorderClassNames = {
  xs: 'h-full w-5',
  s: 'h-full w-7',
  m: 'h-full w-11',
  l: 'h-full w-[3.25rem]', // equivalent of w-13 which is missing in Tailwind
};

const variantButtonClassNames: Record<ButtonVariantType, string> = {
  primary: 'bg-yellow group-hover:bg-gray-700 border-2 border-[transparent]',
  secondary:
    'border-gray-700 group-hover:bg-gray-700  box-border border-2 ' +
    'dark:group-hover:bg-white dark:border-white',
  tertiary: 'bg-black bg-opacity-8 dark:bg-opacity-24',
  quaternary: '',
  link: 'group-hover:bg-black group-hover:bg-opacity-8  ',
};

const variantButtonWithoutIconClassNames: Record<ButtonVariantType, string> = {
  primary: 'group-hover:bg-gray-700',
  secondary: 'box-border',
  tertiary: 'group-hover:bg-opacity-24 dark:group-hover:bg-opacity-40',
  quaternary: '',
  link: '',
};

const variantTextClassNames: Record<ButtonVariantType, string> = {
  primary: 'text-black group-hover:text-white',
  secondary: 'text-black group-hover:text-white dark:text-white dark:group-hover:text-black',
  tertiary: 'dark:text-white text-black',
  quaternary: 'dark:text-white text-black',
  link: 'underline group-hover:no-underline dark:text-white text-black',
};

const variantIconClassNames: Record<ButtonVariantType, string> = {
  primary: 'bg-yellow text-black',
  secondary:
    'text-black group-hover:text-black group-hover:bg-white ' +
    'dark:text-white dark:group-hover:text-white dark:group-hover:bg-gray-700',
  tertiary:
    'bg-black bg-opacity-0 group-hover:bg-opacity-12 dark:group-hover:bg-opacity-40  dark:text-white text-black',
  quaternary:
    'dark:text-white text-black group-hover:bg-black group-hover:bg-opacity-8 dark:group-hover:bg-gray-700 ',
  link: 'dark:text-white text-black',
};

const variantIconWithoutTextClassNames: Record<ButtonVariantType, string> = {
  primary: 'bg-yellow text-black justify-center group-hover:bg-gray-700 group-hover:text-white',
  secondary:
    'justify-center text-black group-hover:text-white group-hover:bg-gray-700' +
    ' dark:text-white dark:group-hover:text-black dark:group-hover:bg-white',
  tertiary: 'justify-center bg-black bg-opacity-0 group-hover:bg-opacity-12',
  quaternary:
    'justify-center dark:text-white text-black group-hover:bg-black group-hover:bg-opacity-8 dark:group-hover:bg-gray-700',
  link: 'justify-center',
};

const getButtonClassNames = ({
  size,
  variant,
  iconPlacement,
  noKeyboard,
  showSkeletonLoader,
}: ButtonProps) =>
  classNames(
    'flex items-center rounded-full font-bold group-focus-visible:button justify-between',
    iconPlacement === 'left' && 'flex-row-reverse',
    sizeButtonClassNames[size],
    variantButtonClassNames[variant],
    Boolean(iconPlacement) && variantButtonWithoutIconClassNames[variant],
    animationClassName,
    { 'group-focus-visible:outline-none': noKeyboard },
    showSkeletonLoader && 'animate-glow'
  );

const getTextClassNames = ({ size, variant, iconPlacement, linkUnderline }: ButtonProps) =>
  classNames(
    'mx-auto font-bold text-nowrap',
    iconPlacement ? sizeTextWithIconClassNames[size][iconPlacement] : sizeTextClassNames[size],
    variantTextClassNames[variant],
    animationClassName,
    { 'group-hover:underline': linkUnderline }
  );

const getIconClassNames = ({ size, variant, iconPlacement, children }: ButtonProps) =>
  classNames(
    'flex items-center rounded-full relative',
    iconPlacement === 'left' && 'justify-end',
    variantWithBorders.includes(variant) && !children
      ? sizeIconWithBorderClassNames[size]
      : sizeIconClassNames[size],
    !iconPlacement && children ? 'hidden' : undefined,
    !children ? variantIconWithoutTextClassNames[variant] : variantIconClassNames[variant],
    animationClassName
  );

const animationClassName = 'transition-colors ease-linear duration-300';

export const Button = ({
  size,
  variant,
  busy,
  className,
  iconPlacement,
  disabled,
  children,
  icon,
  linkUnderline,
  noKeyboard,
  showSkeletonLoader,
  ...rest
}: ButtonProps): ReactElement => {
  const { getURLWithLocalePrefix } = useFallbackTranslation();
  const iconElement = (
    <div className={getIconClassNames({ size, variant, iconPlacement, children })}>
      {busy && (
        <div className="m-auto size-6 animate-spin rounded-full border-2 border-black border-t-[transparent] dark:border-white"></div>
      )}
      {!busy && (
        <div
          className={classNames(
            'absolute flex size-6 items-center justify-center transition-[transform,left,right] duration-300 ease-linear',
            children
              ? iconPlacement === 'right'
                ? 'left-0 group-hover:left-1/2 group-hover:[transform:translateX(-50%)]'
                : 'right-0 group-hover:right-1/2 group-hover:[transform:translateX(50%)]'
              : ''
          )}
        >
          {icon}
        </div>
      )}
    </div>
  );

  const baseProps = {
    ...rest,
    className: classNames('group focus-visible:outline-none', className),
  };
  const interactiveProps = {
    ...baseProps,
    disabled: disabled || busy,
    tabIndex: noKeyboard ? -1 : 0,
    className: classNames(baseProps.className, 'disabled:opacity-80 disabled:cursor-not-allowed'),
  };

  const child = (
    <div
      className={getButtonClassNames({
        size,
        variant,
        iconPlacement,
        noKeyboard,
        showSkeletonLoader,
      })}
    >
      {children && (
        <div className={getTextClassNames({ size, variant, iconPlacement, linkUnderline })}>
          {children}
        </div>
      )}
      {iconElement}
    </div>
  );

  if ((rest as LinkProps).asLink) {
    const { href, className, ...linkRest } = interactiveProps as LinkProps;
    return (
      <a
        className={classNames(className, '!no-underline')}
        href={getURLWithLocalePrefix(href || '')}
        {...omit(linkRest, 'asLink')}
      >
        {child}
      </a>
    );
  }

  if ((rest as DivProps).asDiv) {
    const divProps = omit(baseProps, 'asDiv') as React.HTMLAttributes<HTMLDivElement>;
    return <div {...divProps}>{child}</div>;
  }

  return <button {...(interactiveProps as BtnProps)}>{child}</button>;
};
